(() => {
  const JQUERY_URL = 'https://cdn.jsdelivr.net/npm/jquery@3.7.1/dist/jquery.min.js';
  const SLICK_JS_URL = 'https://cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.min.js';
  const SLICK_CSS_URL = 'https://cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.css';

  const loadScript = (src, id) => {
    const existing = document.getElementById(id);
    if (existing) {
      return new Promise((resolve, reject) => {
        if (existing.dataset.loaded === 'true') return resolve();
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.async = true;
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });
  };

  const loadStylesheet = (href, id) => {
    const existing = document.getElementById(id);
    if (existing) {
      if (existing.dataset.loaded === 'true' || existing.sheet) return Promise.resolve();
      return new Promise((resolve, reject) => {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = href;
      link.addEventListener('load', () => {
        link.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      link.addEventListener('error', reject, { once: true });
      document.head.appendChild(link);
    });
  };

  const ensureDependencies = async () => {
    await loadStylesheet(SLICK_CSS_URL, 'oss-slick-css');
    if (!window.jQuery) await loadScript(JQUERY_URL, 'oss-jquery');
    if (!window.jQuery.fn || !window.jQuery.fn.slick) await loadScript(SLICK_JS_URL, 'oss-slick-js');
    return window.jQuery;
  };

  if (customElements.get('opposite-split-slider')) return;

  class OppositeSplitSlider extends HTMLElement {
    waitForMeasurableLayout(maxFrames = 30) {
      return new Promise((resolve) => {
        let frame = 0;
        const check = () => {
          const height = this.panes[0] ? this.panes[0].getBoundingClientRect().height : 0;
          if (height > 10 || frame >= maxFrames || !this.isConnected) {
            resolve();
            return;
          }
          frame += 1;
          window.requestAnimationFrame(check);
        };
        check();
      });
    }

    async connectedCallback() {
      if (this.initialized || this.initializing) return;
      this.initializing = true;
      this.panes = Array.from(this.querySelectorAll('.oss__pane'));
      this.count = this.panes[0] ? this.panes[0].querySelectorAll('.oss__slide').length : 0;
      this.interval = Math.max(2000, Number(this.dataset.interval) || 5000);
      this.duration = Math.max(1, Number(this.dataset.duration) || 800);
      this.autoplay = this.dataset.autoplay === 'true';
      this.pauseOnHover = this.dataset.pauseHover === 'true';
      this.useEasing = this.dataset.useEasing === 'true';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.isMobile = this.mobileQuery.matches;

      try {
        this.$ = await ensureDependencies();
        if (!this.isConnected) return;
        await this.waitForMeasurableLayout();
        if (!this.isConnected) return;
        this.initializeSlick();
        this.bindEvents();
        this.initialized = true;
        this.start();
      } catch (error) {
        console.error('Opposite Split Slider: Slickの読み込みに失敗しました。', error);
      } finally {
        this.initializing = false;
      }
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.handleVisibility);
      this.mobileQuery.removeEventListener('change', this.handleBreakpointChange);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.destroySlick();
      this.initialized = false;
    }

    bindEvents() {
      this.handleVisibility = () => document.hidden ? this.stop() : this.start();
      this.handleBreakpointChange = (event) => {
        this.isMobile = event.matches;
        this.stop();
        this.destroySlick();
        this.initializeSlick();
        this.start();
      };
      this.handleMouseEnter = () => this.stop();
      this.handleMouseLeave = () => this.start();
      document.addEventListener('visibilitychange', this.handleVisibility);
      this.mobileQuery.addEventListener('change', this.handleBreakpointChange);

      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const isThemeEditor = Boolean(window.Shopify && window.Shopify.designMode);
      if (this.pauseOnHover && canHover && !isThemeEditor) {
        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);
      }
    }

    orderSlides(pane, reverse) {
      const slides = Array.from(pane.querySelectorAll(':scope > .oss__slide'))
        .sort((a, b) => Number(a.dataset.slideIndex) - Number(b.dataset.slideIndex));
      const ordered = reverse && slides.length > 1 ? [slides[0], ...slides.slice(1).reverse()] : slides;
      ordered.forEach((slide) => pane.appendChild(slide));
    }

    initializeSlick() {
      if (!this.$ || this.count < 1) return;
      this.panes.forEach((pane, index) => {
        const reverse = this.isMobile ? index === 0 : index === 1;
        this.orderSlides(pane, reverse);
        this.$(pane).slick({
          rows: 0,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: false,
          infinite: this.count > 1,
          autoplay: false,
          speed: this.reduceMotion ? 1 : this.duration,
          cssEase: this.useEasing ? 'cubic-bezier(.65, 0, .35, 1)' : 'linear',
          vertical: !this.isMobile,
          verticalSwiping: !this.isMobile,
          swipe: false,
          draggable: false,
          touchMove: false,
          pauseOnHover: false,
          waitForAnimate: true,
          adaptiveHeight: false,
          accessibility: index === 0
        });
      });
    }

    destroySlick() {
      if (!this.$ || !this.panes) return;
      this.panes.forEach((pane) => {
        if (this.$(pane).hasClass('slick-initialized')) this.$(pane).slick('unslick');
      });
    }

    advance() {
      if (!this.$ || this.count < 2) return;
      this.$(this.panes[0]).slick(this.isMobile ? 'slickPrev' : 'slickNext');
      this.$(this.panes[1]).slick(this.isMobile ? 'slickNext' : 'slickPrev');
    }

    start() {
      this.stop();
      if (!this.autoplay || this.count < 2 || document.hidden || !this.$) return;
      this.timer = window.setInterval(() => this.advance(), this.interval);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('opposite-split-slider', OppositeSplitSlider);
})();
